/**
 * The request id a surface that owns its OWN retry sends (#1235).
 *
 * ## Why this exists at all
 *
 * `api()` mints a fresh id per call (`lib/api.ts`), which is exactly right for
 * the generic outbox: the outbox freezes the body at enqueue time and replays
 * those same bytes under that same id, so the attempt and the replay are one
 * request by construction.
 *
 * A surface that keeps its own dirty-row state machine — the list sheet, the
 * Food grid — does not work that way. It is `mustBeOnline`, so nothing is
 * frozen; it retries by CALLING AGAIN with whatever the row holds now. A fresh
 * id per call means the retry names a request the server never saw on the
 * attempt that may already have committed, and the server's ledger cannot help.
 * The id has to be derived instead.
 *
 * ## The rule
 *
 * **A key names a request's BYTES, not a user's intention.** So the key is
 * derived from the exact body about to be sent, plus a caller-supplied scope
 * that says which logical rows this body is for:
 *
 * - Retry with the same rows and the same values → same key → the server
 *   answers from its ledger and nothing is written twice.
 * - Retry after the user edited during the outage → different bytes → a NEW
 *   key, deliberately. It has to be: the recorded response describes a
 *   DIFFERENT batch, and the list sheet maps `added` back to its draft rows by
 *   POSITION, so replaying it against a re-composed batch would hand one row
 *   the id that belongs to another. A wrong id is worse than a duplicate row —
 *   it is a row on screen that edits and deletes somebody else.
 * - A genuinely new batch → different scope (the rows are new rows, with new
 *   keys) → a new key, even when the values happen to be identical. This is why
 *   `scope` is not optional: typing "Milk" into a second row produces the same
 *   BODY as the first row did, and hashing the body alone would swallow it.
 * - A scope whose last request was ANSWERED → a new key, even at byte-identical
 *   values. See `retireRequestScope` below; this is the rule that stops a
 *   *second* write under a reusable scope being swallowed as a replay of the
 *   first.
 *
 * ### Bytes are not enough on their own: the answered-request rule
 *
 * Derivation from (scope, body) alone says nothing about TIME, and a scope that
 * can legitimately be written twice needs it to. The Food grid is exactly that
 * shape: its scope is the product row, so "set target 2 → clear the target,
 * which DELETES the plan → set target 2 again" is three writes, and the first
 * and third have the identical scope and the identical body. A pure derivation
 * hands them one key, the server's ledger answers the third from the first's
 * recorded response, and the row is handed the DTO of a plan that no longer
 * exists: nothing was written, the target vanishes on reload, and the next edit
 * PATCHes a deleted id. That is worse than the duplicate this file prevents,
 * and it needs no network failure at all — it is the online happy path.
 *
 * So a key is retired the moment the request it names is answered. A retry can
 * only ever happen for a request that was NOT answered — that is what a retry
 * is — so retirement never touches the case this whole file exists for, and it
 * closes every "same scope, same bytes, genuinely a second write" case at once,
 * including the one where another member deleted the plan out from under us.
 *
 * ### The residual, stated
 *
 * The second bullet leaves one case open: the first attempt committed, its
 * response was lost, the user then edited the row, and the retry goes up under
 * a new key — so the edited row is created a second time. Keying cannot close
 * it, because the two requests genuinely ARE different requests. Closing it
 * needs the ROUTE to recognise the row it already made (as `POST /stock-plans`
 * does through its `(household, product)` unique index, and
 * `PATCH /products/bulk` through its normalized-name index). The shopping-list
 * bulk add deliberately has no such constraint — two "Milk" rows on one list is
 * a thing people want — so for that route the window is real and bounded: it
 * needs an edit made between a lost response and the next flush pass.
 *
 * ### The caller that opts out of the bytes, and why (#1533)
 *
 * The Food grid's CREATE rows pass a null body and put everything in the scope,
 * so their key is derived from the draft row's identity alone. That is not a
 * shortcut — it is the second bullet's tradeoff taken the other way, and it is
 * available there because the scope names something real: one draft row of one
 * mount of one grid, which the user cannot type a second one of.
 *
 * A create has no natural key, so a key that tracked its bytes would let every
 * keystroke made during an outage mint a new identity for the same intended
 * food, and the normalized-name index cannot catch the ones that normalize to
 * nothing. Identity-only keying costs the mirror case instead: a draft edited
 * between a lost response and the retry is answered with the row as first
 * created, and the user edits it from there. One visible stale value beats a
 * silently forked catalog.
 *
 * The grid's UPDATE rows keep the bytes rule, for the reason the second bullet
 * gives: a row that already exists can be written again safely, and answering a
 * changed update from the ledger would report the newer values as saved and then
 * paint the older ones back over them.
 *
 * The third bullet used to leave a WIDER one, and that one is now closed at the
 * caller rather than here. A batch retry that had absorbed a row typed during
 * the outage was a re-composed batch, hence a new scope, hence a new key — so
 * the rows the lost attempt had already committed landed a second time, which
 * is the issue's own headline scenario with one extra keystroke in it. The list
 * sheet now re-sends a failed create batch AS ITSELF and leaves the new row for
 * the next pass; see `createCandidates` in `ListSheet.tsx`.
 *
 * ## Scoping, and why the salt is not decoration
 *
 * The server's ledger identity is `(household_id, request_id)` — see
 * `apps/api/src/services/idempotency.ts`. A derived key is a pure function of
 * its inputs, so two members of ONE household typing the same rows would
 * otherwise mint the same key, and the second member's write would be answered
 * with the first member's rows. The per-session salt makes that impossible
 * while leaving the key stable across every retry within the session that owns
 * the write — which is the whole lifetime of these surfaces' retry loops, since
 * a `mustBeOnline` write is not durable across a reload anyway.
 */

/**
 * One opaque value per module load, i.e. per tab.
 *
 * Not persisted on purpose. Persisting it would make keys survive a reload,
 * but the dirty rows they belong to do not: these surfaces hold their pending
 * writes in memory and warn on unload. A salt that outlived the state it
 * scopes would only widen the window for an accidental match.
 */
const SESSION_SALT = (() => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    // Fall through to the non-crypto id below.
  }
  return `s-${Date.now()}-${Math.random().toString(16).slice(2)}`;
})();

/**
 * A 53-bit string hash (cyrb53).
 *
 * Not cryptographic and does not need to be: nothing here is a secret, and a
 * collision is only reachable between two bodies sent under the SAME scope
 * within one tab, where "same scope" already means the same rows of the same
 * list. It is written out rather than pulled from `crypto.subtle` because that
 * API is async, and the key has to be computed on the line that builds the
 * request.
 */
function hash(value: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let index = 0; index < value.length; index += 1) {
    const ch = value.charCodeAt(index);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/**
 * How many requests under a given scope have already been answered.
 *
 * Module scope, i.e. per tab, and deliberately alongside `SESSION_SALT`: it has
 * to outlive React state, because the state that would otherwise hold it does
 * not survive the background refresh that follows every commit. A grid row
 * rebuilt from a freshly loaded product has no memory of the plan it created a
 * moment ago, and that is precisely the moment the reused key would bite.
 *
 * Bounded by the number of distinct scopes a tab actually completes a write
 * under — one small entry per such scope — so it is not worth evicting from.
 */
const answeredByScope = new Map<string, number>();

/**
 * Declare that the request this scope last named has been ANSWERED, so the next
 * write under it is a new request rather than a replay of that one.
 *
 * Call it on success, and only on success. A failed write may or may not have
 * committed — that ambiguity is the entire subject of this file — and its retry
 * must keep the key it was sent under.
 *
 * The caller for a scope that can only ever be written once (the list sheet:
 * its row keys come from a monotonic counter and are never reused) does not
 * need this. The caller for a scope keyed by a durable entity (the Food grid:
 * its scope is the product row, which outlives any number of plans) does.
 */
export function retireRequestScope(scope: string): void {
  answeredByScope.set(scope, (answeredByScope.get(scope) ?? 0) + 1);
}

/**
 * The id to send with a write this surface will retry itself.
 *
 * `scope` names the logical request — the endpoint plus whatever identifies the
 * rows it is for (the sheet passes its stable row keys). `body` is the payload
 * exactly as it will be serialized. Two calls agree on a key when, and only
 * when, both agree AND no request under that scope has been answered between
 * them (`retireRequestScope`).
 *
 * The length is bounded well inside the server's `MAX_REQUEST_ID_LENGTH` (200):
 * the scope is hashed rather than embedded, so a hundred-row batch is the same
 * size as a one-row one, and the generation is inside that same hash.
 */
export function stableRequestKey(scope: string, body: unknown): string {
  const generation = answeredByScope.get(scope) ?? 0;
  return `k${SESSION_SALT}-${hash(`${scope}|g${generation}`)}-${hash(JSON.stringify(body) ?? "")}`;
}

/** Test seam: forget every retirement, so one spec cannot key off another's. */
export function resetRetiredRequestScopesForTest(): void {
  answeredByScope.clear();
}
